export const TextConfig = {

	"brandName": "TurboSteed",

	"heroTitle": "Web Hosting Built for AI Coding Agents",
	"heroDescription": "TurboSteed gives your coding agent a ready-made MCP and Skill file that deploy and host what it builds — with a built-in CMS and contact-form backend. No infra to manage.",
	"heroCta": {
		"primary": { "text": "Start Now", "url": "https://dashboard.turbosteed.com" },
		"secondary": { "text": "Read the Docs", "url": "/docs" }
	},

	"mcpConfig": {
		"headline": "Add to your .mcp.json config:",
		"snippet": `{
  "mcpServers": {
    "turbosteed": {
      "command": "turbosteed",
      "args": ["--mcp"],
      "env": {
        "TURBOSTEED_ACCOUNT_SID": "your_account_sid",
        "TURBOSTEED_API_KEY": "your_api_key",
        "TURBOSTEED_SITE_ID": "your_site_id"
      }
    }
  }
}`
	},

	"worksWithBadge": "Tested and verified with Claude Code",

	"howItWorks": [
		{
			"step": 1,
			"title": "Create an Account",
			"description": "Sign up for free on the TurboSteed dashboard."
		},
		{
			"step": 2,
			"title": "Configure MCP",
			"description": "Get your credentials from the dashboard and configure your MCP."
		},
		{
			"step": 3,
			"title": "Setup TurboSteed Skill",
			"description": "Install the TurboSteed skill in your Claude Code environment."
		},
		{
			"step": 4,
			"title": "Provide Requirements",
			"description": "Describe your site. The skill creates, uploads assets, CMS entries, and deploys."
		}
	],

	"cliInstall": {
		"command": "npm install -g @turbosteed/cli",
		"npmUrl": "https://www.npmjs.com/package/@turbosteed/cli"
	},

	"personas": [
		{
			"usecase": "For Developers",
			"persona": "Freelancers and agency developers who want to ship faster",
			"icon": "fa fa-code",
			"features": [
				"Skip WordPress complexity — deploy React, Vue, or HTML instantly",
				"No server config, no hosting management",
				"Assets on global CDN — no worries about speed",
				"Manage more clients with a single vendor and AI efficiency",
				"MCP + REST API for full automation"
			],
			"cta": { "text": "Get Started", "url": "https://dashboard.turbosteed.com" }
		},
		{
			"usecase": "For Agencies",
			"persona": "Deliver client projects faster with less overhead",
			"icon": "fa fa-building",
			"features": [
				"Deploy client sites in minutes, not days",
				"Visual CMS — no developer bottleneck for content edits",
				"Mobile app for site owners — fewer 'send me the stats' requests",
				"One dashboard for all client sites",
				"AI automation reduces repetitive work",
				"Professional handoff experience"
			],
			"cta": { "text": "Learn More", "url": "/developers-and-agencies" }
		}
	],

	"features": [
		{
			"name": "AI-Driven Site Creation",
			"slug": "ai-site-creation",
			"tagline": "Build Sites with Claude Code",
			"description": "Use Claude Code with the TurboSteed MCP server and Skill to build complete websites from natural language descriptions.",
			"icon": "icon-genius",
			"capabilities": [
				"Connect Claude Code via MCP",
				"Use TurboSteed Skill file",
				"Build static HTML/CSS/JS sites",
				"Deploy in one command"
			]
		},
		{
			"name": "CMS",
			"slug": "cms",
			"tagline": "Headless CMS for AI Agents",
			"description": "Define schemas, manage entries, and serve content via API. Create and publish content programmatically through MCP or REST API.",
			"icon": "icon-documents",
			"capabilities": [
				"Create schemas via MCP or API",
				"Multiple field types supported",
				"Publish entries programmatically",
				"Global CDN delivery"
			]
		},
		{
			"name": "Built-in Analytics",
			"slug": "analytics",
			"tagline": "Understand Your Visitors",
			"description": "Track visitor counts, traffic patterns, and popular pages. Make data-driven decisions about your content.",
			"icon": "icon-presentation",
			"capabilities": [
				"Visitor count tracking",
				"7, 14, and 30-day views",
				"Traffic source analysis",
				"Popular pages identification"
			]
		},
		{
			"name": "Contact Forms",
			"slug": "contact-forms",
			"tagline": "Capture Leads",
			"description": "Built-in contact forms and custom form types. Receive submissions via dashboard and trigger webhooks.",
			"icon": "icon-notebook",
			"capabilities": [
				"Built-in contact forms",
				"Custom form types",
				"Dashboard submissions",
				"Webhook triggers"
			]
		},
		{
			"name": "Asset Library",
			"slug": "asset-library",
			"tagline": "Manage Your Images and Files",
			"description": "Upload assets via API or dashboard. Assets are served from the global CDN.",
			"icon": "icon-picture",
			"capabilities": [
				"Upload via API or dashboard",
				"Global CDN delivery",
				"Gallery view organization",
				"Quick URL copying"
			]
		},
		{
			"name": "Custom Domains",
			"slug": "custom-domains",
			"tagline": "Use Your Own Domain",
			"description": "Connect your existing domain. SSL certificate included automatically for secure connections.",
			"icon": "icon-browser",
			"capabilities": [
				"Connect existing domains",
				"Free SSL certificate",
				"Domain verification",
				"HTTPS by default"
			]
		},
		{
			"name": "Mobile App",
			"slug": "mobile-app",
			"tagline": "Manage Your Site On The Go",
			"description": "View site statistics and read contact form submissions from your phone. Available on Android (experimental).",
			"icon": "icon-mobile",
			"capabilities": [
				"View visitor analytics",
				"Read contact form submissions",
				"Check site statistics",
				"Android app (experimental)"
			]
		},
		{
			"name": "Zip Upload",
			"slug": "zip-upload",
			"tagline": "Upload Your Own Code",
			"description": "Build with any tools you prefer. Upload your static HTML, CSS, and JS as a zip file. No vendor lock-in.",
			"icon": "icon-upload",
			"capabilities": [
				"Support for HTML/CSS/JS",
				"Static site uploads",
				"CLI deployment",
				"No vendor lock-in"
			]
		},
		{
			"name": "API Access",
			"slug": "api-access",
			"tagline": "Full Programmatic Access",
			"description": "REST API for site management, CMS, content updates, and asset management. Integrate with your existing tools.",
			"icon": "icon-layers",
			"capabilities": [
				"Site configuration API",
				"CMS schemas and entries",
				"Asset uploads",
				"Form data retrieval"
			]
		},
		{
			"name": "Webhooks",
			"slug": "webhooks",
			"tagline": "Automate Your Workflows",
			"description": "Trigger workflows on form submissions. Connect to Zapier, n8n, or custom endpoints.",
			"icon": "icon-refresh",
			"capabilities": [
				"Form submission triggers",
				"Connect to Zapier/n8n",
				"Custom endpoint support",
				"Real-time notifications"
			]
		},
		{
			"name": "CI/CD",
			"slug": "cicd",
			"tagline": "Deploy from Terminal",
			"description": "Install @turbosteed/cli via npm. Deploy sites and manage content from your terminal or CI/CD pipeline.",
			"icon": "icon-tools",
			"capabilities": [
				"Install via npm",
				"Deploy with one command",
				"CI/CD pipeline integration",
				"Programmatic site management"
			]
		}
	],

	"industries": [
		{
			"name": "Restaurants & Cafes",
			"slug": "restaurants",
			"tagline": "Websites for Restaurants, Cafes & Food Businesses",
			"description": "Showcase your menu, ambiance, and location. Perfect for restaurants, cafes, bakeries, and food trucks.",
			"icon": "fa fa-cutlery",
			"templates": ["RestaurantTemplate-1", "CafeTemplate-1"],
			"benefits": [
				"Display your menu beautifully",
				"Show location and hours prominently",
				"Contact forms for reservations"
			]
		},
		{
			"name": "Fitness & Wellness",
			"slug": "fitness",
			"tagline": "Websites for Gyms, Yoga Studios & Wellness Centers",
			"description": "Display classes, trainer profiles, and membership information. Ideal for gyms, yoga studios, and wellness centers.",
			"icon": "fa fa-heartbeat",
			"templates": ["GymTemplate", "YogaTemplate-1"],
			"benefits": [
				"Showcase classes and schedules",
				"Highlight trainer profiles",
				"Capture membership inquiries"
			]
		},
		{
			"name": "Beauty & Salons",
			"slug": "beauty",
			"tagline": "Websites for Salons, Spas & Beauty Professionals",
			"description": "Showcase your services, portfolio, and pricing. Perfect for salons, spas, and beauty professionals.",
			"icon": "fa fa-scissors",
			"templates": ["BeautyProducts", "Salon-1"],
			"benefits": [
				"Display services and pricing",
				"Gallery for portfolio/work",
				"Easy appointment inquiries"
			]
		},
		{
			"name": "Hotels & Travel",
			"slug": "hospitality",
			"tagline": "Websites for Hotels, Travel Agencies & Tourism",
			"description": "Showcase accommodations, destinations, and packages. Ideal for hotels, travel agencies, and tour operators.",
			"icon": "fa fa-plane",
			"templates": ["HotelTemplate", "TravelAgencyTemplate-1"],
			"benefits": [
				"Stunning property galleries",
				"Destination showcases",
				"Booking inquiry forms"
			]
		},
		{
			"name": "Professional Services",
			"slug": "consulting",
			"tagline": "Websites for Consultants, Coaches & Professionals",
			"description": "Establish your expertise and attract clients. Perfect for consultants, coaches, and professional service providers.",
			"icon": "fa fa-briefcase",
			"templates": ["ConsultantTemplate", "CoachingClassesTemplate"],
			"benefits": [
				"Showcase your expertise",
				"Client testimonials",
				"Contact forms for inquiries"
			]
		},
		{
			"name": "Creative & Portfolio",
			"slug": "portfolio",
			"tagline": "Websites for Creatives, Artists & Designers",
			"description": "Display your creative work in stunning galleries. Ideal for photographers, designers, and artists.",
			"icon": "fa fa-camera",
			"templates": ["PortfolioTemplate-1", "FashionDesignerTemplate-1", "turbo-template-1"],
			"benefits": [
				"Beautiful portfolio galleries",
				"Project showcases",
				"Professional presentation"
			]
		},
		{
			"name": "Architecture & Design",
			"slug": "architecture",
			"tagline": "Websites for Architects, Interior Designers & Furniture",
			"description": "Showcase your designs and projects professionally. Perfect for architects, interior designers, and furniture showrooms.",
			"icon": "fa fa-building-o",
			"templates": ["ArchitectTemplate", "InteriorDesignerTemplate-1", "FurnitureShowroom"],
			"benefits": [
				"Project portfolio display",
				"Design philosophy showcase",
				"Client inquiry forms"
			]
		}
	],

	"socialProof": {
		"heading": "Why businesses choose TurboSteed",
		"values": [
			{ "text": "Freedom to choose your tech stack", "icon": "fa fa-code" },
			{ "text": "No infrastructure to manage", "icon": "fa fa-server" },
			{ "text": "AI-powered velocity", "icon": "fa fa-bolt" },
			{ "text": "Improved engineering productivity", "icon": "fa fa-rocket" },
			{ "text": "Transparent pricing", "icon": "fa fa-dollar" },
			{ "text": "Reliable hosting", "icon": "fa fa-shield" }
		]
	},

	"hiddenValue": {
		"value": "You focus on site design and content",
		"how": "Leave hardware, storage, network and scaling to us"
	},

	"benefits": [
		{
			"feature": "Site Management via MCP",
			"value": "Full site control via MCP — manage pages, content, assets, and deployments",
			"icon": "icon-layers",
			"aria-label": "Site management via Model Context Protocol",
			"readmeUrl": "/docs/mcp-guide"
		},
		{
			"feature": "Built-in Forms",
			"value": "Contact forms and custom form types. Submissions flow to dashboard and webhooks",
			"icon": "icon-notebook",
			"aria-label": "Built-in contact and custom forms",
			"readmeUrl": "/docs/user-guide"
		},
		{
			"feature": "Assets & CDN",
			"value": "Upload assets via API. Global CDN ensures fast delivery worldwide",
			"icon": "icon-speedometer",
			"aria-label": "Asset management with global CDN",
			"readmeUrl": "/docs/platform-features"
		},
		{
			"feature": "Build & Deploy via CLI",
			"value": "Freedom to build your site using your tools. Deploy with TurboSteed CLI",
			"icon": "icon-tools",
			"aria-label": "CLI-based deployment",
			"readmeUrl": "/docs/mcp-guide"
		},
		{
			"feature": "Webhooks",
			"value": "Trigger workflows on form submissions. Connect to Zapier, n8n, or custom endpoints",
			"icon": "icon-refresh",
			"aria-label": "Webhook automation",
			"readmeUrl": "/docs/faq"
		},
		{
			"feature": "Visual Dashboard",
			"value": "Non-technical users can view analytics, read forms, and manage content",
			"icon": "icon-presentation",
			"aria-label": "Visual dashboard for site management",
			"readmeUrl": "https://dashboard.turbosteed.com"
		}
	],

	"platformBenefits": [
		{
			"feature": "Global CDN",
			"value": "Fast delivery worldwide",
			"icon": "icon-speedometer"
		},
		{
			"feature": "SSL Included",
			"value": "HTTPS on every site",
			"icon": "icon-shield"
		},
		{
			"feature": "Custom Domains",
			"value": "Connect your own domain",
			"icon": "icon-browser"
		},
		{
			"feature": "Mobile App",
			"value": "Android app (experimental)",
			"icon": "icon-mobile"
		},
		{
			"feature": "Built-in Analytics",
			"value": "Track visitors and pages",
			"icon": "icon-presentation"
		}
	],

	"useCaseDetails": [
		{
			"persona": "Students",
			"value": [
				"Share your assignments with your friends and teachers easily",
				"Create your personal Profile"
			],
			"imgUrl": "/use-cases/developer.png",
			"altText": "TurboSteed Use cases for Students"
		},
		{
			"persona": "Hoteliers",
			"value": [
				"Create a website for your cafe, restaurant hotel",
				"Share your menu as a PDF Link"
			],
			"imgUrl": "/use-cases/restaurant.png",
			"altText": "TurboSteed Use cases for Hoteliers"
		},
		{
			"persona": "Photographers",
			"value": [
				"Showcase your photography skills",
				"Advertise your services"
			],
			"imgUrl": "/use-cases/photography.png",
			"altText": "TurboSteed Use cases for Photographers"
		},
		{
			"persona": "Health and Wellness",
			"value": [
				"Display your services, equipment and pricing plans",
				"Ideal for Gym, Health Club, Wellness or Yoga classes"
			],
			"imgUrl": "/use-cases/gym.png",
			"altText": "TurboSteed Use cases for Gyms, Yoga Classes, Wellness Services and Health Clubs"
		},
		{
			"persona": "Beauticians and Artists",
			"value": [
				"Create an online portfolio and showcase your work",
				"Advertise your professional services"
			],
			"imgUrl": "/use-cases/portfolio.png",
			"altText": "TurboSteed Use cases for Beauticians and Artists"
		}
	],

	"extraBenifit": {
		"key": "Free for 5000 website hits",
		"value": "You pay once your website starts getting traction"
	},

	"inrPrice": {
		"Free Plan": "0",
		"Basic Plan": "90",
		"Startup Plan": "539",
		"Agency Plan": "8549"
	},

	"pricing3col": [
		{
			"plan": "Free Plan",
			"price": {
				"inr": "0",
				"usd": "0"
			},
			"term": "per month",
			"planFeatures": [
				"1 site",
				"3 MB custom site size",
				"25 MB storage",
				"250 visits per month",
				"150 MB data transfer per month",
				"25 contact forms per month",
				"Email support"
			],
			"subscriptionLink": {
				"buttonName": "Start for free",
				"buttonURL": "https://dashboard.turbosteed.com"
			}
		},
		{
			"plan": "Basic Plan",
			"price": {
				"inr": "149",
				"usd": "1.50"
			},
			"term": "per month",
			"planFeatures": [
				"1 site",
				"5 MB custom site size",
				"100 MB storage",
				"1000 visits per month",
				"250 MB data transfer per month",
				"50 contact forms per month",
				"Email support",
				"1 custom domain",
				"Contact forms in email (addon)"
			],
			"subscriptionLink": {
				"buttonName": "Sign Up",
				"buttonURL": "https://dashboard.turbosteed.com"
			}
		},
		{
			"plan": "Startup Plan",
			"price": {
				"inr": "549",
				"usd": "6"
			},
			"term": "per month",
			"planFeatures": [
				"2 sites",
				"25 MB custom site size",
				"250 MB max Assets",
				"25000 visits per month",
				"5 GB data transfer per month",
				"250 contact forms per month",
				"Site Analytics",
				"1 custom domain",
				"Contact forms in email (addon)",
				"Remove TurboSteed branding",
				"Custom 404 page",
				"Email support"
			],
			"subscriptionLink": {
				"buttonName": "Sign Up",
				"buttonURL": "https://dashboard.turbosteed.com"
			}
		},
		{
			"plan": "Agency Plan",
			"price": {
				"inr": "Custom",
				"usd": "Custom"
			},
			"term": "per month",
			"planFeatures": [
				"50 sites",
				"25 MB size per site",
				"150 MB Assets per site",
				"250000 visits per month",
				"100 GB data transfer per month",
				"5000 contact forms per month",
				"Site Analytics",
				"25 custom domains",
				"Contact forms in email (addon)",
				"Custom branding in footer",
				"Custom 404 page",
				"Priority email support"
			],
			"subscriptionLink": {
				"buttonName": "Contact Us",
				"buttonURL": "/contactus"
			}
		}
	],

	"pricing3colYr": [
		{
			"plan": "Free Plan",
			"price": {
				"inr": "0",
				"discountINR": "0",
				"usd": "0",
				"discountUSD": "0"
			},
			"term": "per year",
			"planFeatures": [
				"1 site",
				"3 MB custom site size",
				"25 MB storage",
				"250 visits per month",
				"150 MB data transfer per month",
				"25 contact forms per month",
				"Email support"
			],
			"subscriptionLink": {
				"buttonName": "Start for free",
				"buttonURL": "https://dashboard.turbosteed.com"
			}
		},
		{
			"plan": "Basic Plan",
			"price": {
				"inr": "1489",
				"discountINR": "1789",
				"usd": "15",
				"discountUSD": "18"
			},
			"term": "per year",
			"planFeatures": [
				"1 site",
				"5 MB custom site size",
				"100 MB storage",
				"1000 visits per month",
				"250 MB data transfer per month",
				"50 contact forms per month",
				"Email support",
				"1 custom domain (addon)",
				"Contact forms in email (addon)"
			],
			"subscriptionLink": {
				"buttonName": "Sign Up",
				"buttonURL": "https://dashboard.turbosteed.com"
			}
		},
		{
			"plan": "Startup Plan",
			"price": {
				"inr": "5489",
				"discountINR": "6589",
				"usd": "59",
				"discountUSD": "72"
			},
			"term": "per year",
			"planFeatures": [
				"2 sites",
				"25 MB custom site size",
				"250 MB max Assets",
				"25000 visits per month",
				"5 GB data transfer per month",
				"250 contact forms per month",
				"Site Analytics",
				"1 custom domain",
				"Contact forms in email (addon)",
				"Custom 404 page",
				"Email support"
			],
			"subscriptionLink": {
				"buttonName": "Sign Up",
				"buttonURL": "https://dashboard.turbosteed.com"
			}
		},
		{
			"plan": "Agency Plan",
			"price": {
				"inr": "Custom",
				"usd": "Custom"
			},
			"term": "per year",
			"planFeatures": [
				"50 sites",
				"25 MB size per site",
				"150 MB Assets per site",
				"250000 visits per month",
				"100 GB data transfer per month",
				"5000 contact forms per month",
				"Site Analytics",
				"25 custom domains",
				"Contact forms in email (addon)",
				"Custom 404 page",
				"Priority email support"
			],
			"subscriptionLink": {
				"buttonName": "Contact Us",
				"buttonURL": "/contactus"
			}
		}
	],

	"secondaryValueProp": "Scale without worries",
	"secondaryValuePropDescription": "TurboSteed provides you a hassle free, scalable and reliable platform so that you can focus on your work"
};

export const BrandName = "TurboSteed";
